<template>
	<transition name="toggle-fade">
		<div class="inactivity" v-if="open" @click="handleDismiss" @touchstart="handleDismiss">
			<video ref="screensaverVid" class="screensaver-video" crossorigin="anonymous" poster="@/images/ScreensaverPortrait.webp" autoplay muted loop playsinline tabindex="-1" aria-hidden="true">
				<source src="/videos3/ScreensaverPortrait-subtitles.mp4" type="video/mp4" />
			</video>
			<div class="back-to-app">
				<touch-icon aria-hidden="true" />
				<span>{{ $t('common.backToApp') }}</span>
			</div>
		</div>
	</transition>
</template>

<script setup>
import { useStore } from '@/store';
import debounce from 'lodash.debounce';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const windowEvents = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress', 'kiosk-activity'];
const documentEvents = ['touchstart', 'pointerdown'];
const warningLength = 10 * 1000; // milliseconds

const store = useStore();
const router = useRouter();
const states = {
	idle: 'idle',
	inactive: 'inactive',
	warning: 'warning',
};
const state = ref('closed');
const open = computed(() => state.value === states.warning && !store.showInstallBanner && !store.serviceWorkerUpdate);
const onHomePage = computed(() => router.currentRoute.value.fullPath === '/');

let timeout;
const restartInactivityTimer = debounce(event => {
	if (event?.type === 'scroll' && open.value) return;
	state.value = states.inactive;
	clearTimeout(timeout);
	timeout = setTimeout(timeoutExpired, store.settings.inactivityMinutes * 60 * 1000);
}, 50);

function timeoutExpired() {
	clearTimeout(timeout);
	if (state.value === states.inactive) {
		state.value = states.warning;
		timeout = setTimeout(timeoutExpired, warningLength);
	} else if (state.value === states.warning) {
		// Only navigate to home if screensaver would actually be visible
		// (i.e., no install or refresh prompts are showing)
		if (!onHomePage.value && !store.showInstallBanner && !store.serviceWorkerUpdate) {
			router.push('/');
		}
	} else {
		state.value = states.idle;
	}
}

const screensaverVid = ref(null);

watch(open, async isOpen => {
	if (isOpen) {
		document.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
		// Reinsert the same select to close its native picker without changing its value or bindings.
		const select = document.activeElement?.closest('select');
		select?.replaceWith(select);
		document.activeElement?.blur?.();
		// Prevent body scrolling when screensaver is open
		document.body.style.overflow = 'hidden';
		await nextTick();
		if (screensaverVid.value && typeof screensaverVid.value.play === 'function') {
			const playResult = screensaverVid.value.play();
			if (playResult && typeof playResult.catch === 'function') {
				playResult.catch(() => {});
			}
		}
	} else {
		// Restore body scrolling when screensaver is closed
		document.body.style.overflow = '';
		if (screensaverVid.value) {
			screensaverVid.value.pause();
			screensaverVid.value.currentTime = 0.0;
		}
	}
});

function handleDismiss() {
	restartInactivityTimer();
}

function startTracking() {
	restartInactivityTimer();
	windowEvents.forEach(x => window.addEventListener(x, restartInactivityTimer, { passive: true }));
	documentEvents.forEach(x => document.addEventListener(x, restartInactivityTimer, { passive: true }));
}
function stopTracking() {
	restartInactivityTimer.cancel();
	clearTimeout(timeout);
	state.value = 'closed';
	windowEvents.forEach(x => window.removeEventListener(x, restartInactivityTimer));
	documentEvents.forEach(x => document.removeEventListener(x, restartInactivityTimer));
}

watch([
	() => store.settings.inactivityReset && !store.videoPopupOpen,
	() => store.settings.inactivityMinutes,
], ([enabled]) => enabled ? startTracking() : stopTracking(), { immediate: true });
onUnmounted(stopTracking);
</script>
